import type {
  BranchCondition,
  QuestionStep,
  QuestionType,
  SurveyLinkMode,
  SurveyStep,
} from '../types/survey.js';
import type { SurveyAnswer, SurveyAnswerValue } from '../types/surveyResponse.js';

// Phase 6B (A2) — the survey branching engine. PURE: no I/O, no Date, no
// randomness. Shared by the public client (optimistic render + the F3 confirm
// modal) and the server (authoritative next-step + persistence + results
// content-view derivation). Three exported functions:
//
//   evaluateBranching  — per-answer branch decision (first-match-wins).
//   validateSurveyFlow — compose-time flow validator (the save/send gate).
//   realizePath        — the F3 truncation core + resume + content-view source.
//
// Forward-only branch references (validateSurveyFlow rejects anything else) ⇒
// the flow is a DAG ⇒ realizePath always terminates. The reserved option id
// '__other__' is synthesised at answer time for `allowOther`; authored options
// may not use it (G10).

export const RESERVED_OTHER_OPTION_ID = '__other__';

// ---------- 1) evaluateBranching ----------

export type BranchOutcome =
  | { kind: 'go_to'; targetStepId: string }
  | { kind: 'skip_to_outro' }
  | { kind: 'next' };

// Decide where a question's answer routes. Evaluates the step's rules in order;
// the first whose condition matches fires. No match ⇒ linear next. ContentSteps
// never reach here (the runner flows them to next directly).
export function evaluateBranching(step: QuestionStep, value: SurveyAnswerValue): BranchOutcome {
  for (const rule of step.branching) {
    if (!matchCondition(rule.condition, value)) continue;
    if (rule.action === 'skip_to_outro') return { kind: 'skip_to_outro' };
    if (rule.action === 'go_to' && rule.targetStepId) {
      return { kind: 'go_to', targetStepId: rule.targetStepId };
    }
    // go_to with no target is a malformed rule (validator rejects it); treat as
    // linear rather than throwing in the live flow.
    return { kind: 'next' };
  }
  return { kind: 'next' };
}

function matchCondition(cond: BranchCondition, value: SurveyAnswerValue): boolean {
  switch (value.type) {
    case 'yes_no':
      if (typeof cond.value !== 'boolean') return false;
      if (cond.type === 'equals') return value.value === cond.value;
      if (cond.type === 'not_equals') return value.value !== cond.value;
      return false;
    case 'multiple_choice_single':
      if (cond.type === 'equals') return value.optionId === cond.value;
      if (cond.type === 'not_equals') return value.optionId !== cond.value;
      return false;
    case 'multiple_choice_multi':
      if (cond.type === 'contains') return value.optionIds.includes(String(cond.value));
      return false;
    case 'rating_scale':
      return numericMatch(cond, value.rating);
    case 'number':
      return numericMatch(cond, value.number);
    case 'short_text':
    case 'long_text':
      return textMatch(cond, value.text);
    case 'date':
    case 'name':
      return false; // no branching on these (validator enforces; defensive here)
  }
}

function numericMatch(cond: BranchCondition, n: number): boolean {
  if (typeof cond.value !== 'number') return false;
  switch (cond.type) {
    case 'equals':
      return n === cond.value;
    case 'not_equals':
      return n !== cond.value;
    case 'greater_than':
      return n > cond.value;
    case 'less_than':
      return n < cond.value;
    default:
      return false;
  }
}

function textMatch(cond: BranchCondition, text: string): boolean {
  const norm = text.trim().toLowerCase();
  const target = String(cond.value).trim().toLowerCase();
  if (cond.type === 'equals') return norm === target;
  if (cond.type === 'not_equals') return norm !== target;
  return false;
}

// ---------- 2) validateSurveyFlow ----------

export type FlowErrorCode =
  | 'target_not_found'
  | 'target_not_forward'
  | 'self_reference'
  | 'missing_go_to_target'
  | 'condition_type_mismatch' // R-G + branching on date/name
  | 'reserved_option_id' // authored option using '__other__' (G10)
  | 'duplicate_step_id'
  | 'duplicate_option_id'
  | 'empty_choice_options'
  | 'content_step_has_branching' // G7 — defensive against malformed content steps
  | 'auto_name_misplaced'
  | 'rating_scale_bounds';

export type FlowError = {
  stepId: string | null; // null = survey-level
  ruleId: string | null;
  code: FlowErrorCode;
  message: string;
};

// Conditions each question type may carry (R-G). date/name/content_page → none.
// Exported so the compose-time branching editor (B5) drives its comparator
// dropdown off the same matrix the validator enforces.
export const ALLOWED_CONDITIONS: Record<QuestionType, BranchCondition['type'][]> = {
  yes_no: ['equals', 'not_equals'],
  multiple_choice_single: ['equals', 'not_equals'],
  multiple_choice_multi: ['contains'],
  rating_scale: ['equals', 'not_equals', 'greater_than', 'less_than'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than'],
  short_text: ['equals', 'not_equals'],
  long_text: ['equals', 'not_equals'],
  date: [],
  name: [],
};

function conditionValueTypeOk(stepType: QuestionType, cond: BranchCondition): boolean {
  switch (stepType) {
    case 'yes_no':
      return typeof cond.value === 'boolean';
    case 'multiple_choice_single':
    case 'multiple_choice_multi':
    case 'short_text':
    case 'long_text':
      return typeof cond.value === 'string';
    case 'rating_scale':
    case 'number':
      return typeof cond.value === 'number';
    case 'date':
    case 'name':
      return false;
  }
}

function isConditionCompatible(stepType: QuestionType, cond: BranchCondition): boolean {
  return ALLOWED_CONDITIONS[stepType].includes(cond.type) && conditionValueTypeOk(stepType, cond);
}

// Validate a draft flow. Returns [] when sound. Enforces forward-only refs (⇒ no
// loops), target existence, the R-G condition↔type matrix, reserved-option-id,
// the auto-name placement invariant, and content-step-has-no-branching.
export function validateSurveyFlow(steps: SurveyStep[], linkMode: SurveyLinkMode): FlowError[] {
  const errors: FlowError[] = [];

  // Duplicate step ids.
  const seen = new Set<string>();
  for (const step of steps) {
    if (seen.has(step.id)) {
      errors.push({
        stepId: step.id,
        ruleId: null,
        code: 'duplicate_step_id',
        message: `Duplicate step id "${step.id}".`,
      });
    }
    seen.add(step.id);
  }

  const indexById = new Map<string, number>();
  steps.forEach((s, i) => {
    if (!indexById.has(s.id)) indexById.set(s.id, i);
  });

  // Auto-name placement (§3.5 / G10).
  const nameSteps = steps.filter(
    (s): s is QuestionStep => s.kind === 'question' && s.type === 'name',
  );
  if (linkMode === 'per_location') {
    const first = steps[0];
    const firstIsAutoName =
      first && first.kind === 'question' && first.type === 'name' && first.isAutoInserted;
    if (nameSteps.length !== 1 || !firstIsAutoName) {
      errors.push({
        stepId: first?.id ?? null,
        ruleId: null,
        code: 'auto_name_misplaced',
        message: 'per_location surveys require exactly one auto-inserted name step at position 0.',
      });
    }
  } else {
    for (const ns of nameSteps) {
      errors.push({
        stepId: ns.id,
        ruleId: null,
        code: 'auto_name_misplaced',
        message: 'per_recipient surveys must not contain a name step.',
      });
    }
  }

  // Per-step checks.
  steps.forEach((step, i) => {
    if (step.kind === 'content') {
      // Defensive: ContentStep has no branching field, but a direct API write
      // could smuggle one in. Reject as the more specific code (Confirmation 1).
      const maybe = step as unknown as { branching?: unknown[] };
      if (Array.isArray(maybe.branching) && maybe.branching.length > 0) {
        errors.push({
          stepId: step.id,
          ruleId: null,
          code: 'content_step_has_branching',
          message: 'Content pages cannot carry branching rules.',
        });
      }
      return;
    }

    // Question config checks.
    if (step.type === 'multiple_choice_single' || step.type === 'multiple_choice_multi') {
      const options = step.config.type === step.type ? step.config.options : [];
      if (!options || options.length === 0) {
        errors.push({
          stepId: step.id,
          ruleId: null,
          code: 'empty_choice_options',
          message: 'Multiple-choice questions need at least one option.',
        });
      }
      const optIds = new Set<string>();
      for (const opt of options ?? []) {
        if (opt.id === RESERVED_OTHER_OPTION_ID) {
          errors.push({
            stepId: step.id,
            ruleId: null,
            code: 'reserved_option_id',
            message: `Option id "${RESERVED_OTHER_OPTION_ID}" is reserved.`,
          });
        }
        if (optIds.has(opt.id)) {
          errors.push({
            stepId: step.id,
            ruleId: null,
            code: 'duplicate_option_id',
            message: `Duplicate option id "${opt.id}".`,
          });
        }
        optIds.add(opt.id);
      }
    }
    if (step.type === 'rating_scale' && step.config.type === 'rating_scale') {
      if (step.config.min >= step.config.max) {
        errors.push({
          stepId: step.id,
          ruleId: null,
          code: 'rating_scale_bounds',
          message: 'Rating scale min must be less than max.',
        });
      }
    }

    // Branching rules. Any rule on a date/name step trips condition_type_mismatch
    // (their ALLOWED set is empty) — actively prevents broken authored flows.
    for (const rule of step.branching) {
      if (!isConditionCompatible(step.type, rule.condition)) {
        errors.push({
          stepId: step.id,
          ruleId: rule.id,
          code: 'condition_type_mismatch',
          message: `Condition "${rule.condition.type}" is not valid for a ${step.type} question.`,
        });
      }
      if (rule.action === 'go_to') {
        if (!rule.targetStepId) {
          errors.push({
            stepId: step.id,
            ruleId: rule.id,
            code: 'missing_go_to_target',
            message: 'A "go to" rule needs a target step.',
          });
        } else if (rule.targetStepId === step.id) {
          errors.push({
            stepId: step.id,
            ruleId: rule.id,
            code: 'self_reference',
            message: 'A question cannot branch to itself.',
          });
        } else {
          const targetIdx = indexById.get(rule.targetStepId);
          if (targetIdx === undefined) {
            errors.push({
              stepId: step.id,
              ruleId: rule.id,
              code: 'target_not_found',
              message: `Branch target "${rule.targetStepId}" does not exist.`,
            });
          } else if (targetIdx <= i) {
            errors.push({
              stepId: step.id,
              ruleId: rule.id,
              code: 'target_not_forward',
              message: 'Branch targets must be a later step (forward-only).',
            });
          }
        }
      }
      // skip_to_outro needs no target and is always structurally valid.
    }
  });

  return errors;
}

// ---------- 3) realizePath ----------

export type RealizedPath = {
  // Ordered ids of steps traversed on the realized path, INCLUDING content steps
  // (the source for G8 content-page view derivation on submitted responses).
  visitedStepIds: string[];
  // Answers on the realized path, in traversal order. Off-path answers are excluded.
  validAnswers: SurveyAnswer[];
  // Provided answers that fell OFF the realized path (drives the F3 confirm modal).
  truncatedAnswerStepIds: string[];
  // The next unanswered QUESTION on the path, or null at the outro. Content
  // pages are traversed (counted) but never returned here.
  //
  // Resume lands on the next unanswered question. Content steps along the path
  // are re-shown — they're informational pages, and we explicitly chose not to
  // track which ones were seen (G8). For per_location, this never matters since
  // there's no resume (Q3).
  nextStepId: string | null;
  reachedOutro: boolean;
};

// Walk from step 0, branching off each answered question, until the path reaches
// an unanswered question (⇒ nextStepId) or the outro (⇒ reachedOutro). Pure
// recompute — callers diff two RealizedPaths to compute truncation on revise.
export function realizePath(steps: SurveyStep[], answers: SurveyAnswer[]): RealizedPath {
  const indexById = new Map<string, number>();
  steps.forEach((s, i) => {
    if (!indexById.has(s.id)) indexById.set(s.id, i);
  });
  const answerByStep = new Map<string, SurveyAnswer>();
  for (const a of answers) answerByStep.set(a.stepId, a);

  const visitedStepIds: string[] = [];
  const validAnswers: SurveyAnswer[] = [];
  let cursor = 0;
  let reachedOutro = false;
  let nextStepId: string | null = null;

  // Forward-only ⇒ cursor is monotonic; cap is a defensive backstop for
  // unvalidated input.
  const maxIterations = steps.length + 1;
  for (let iter = 0; iter <= maxIterations; iter++) {
    if (cursor >= steps.length) {
      reachedOutro = true;
      break;
    }
    const step = steps[cursor]!;

    if (step.kind === 'content') {
      visitedStepIds.push(step.id);
      cursor += 1;
      continue;
    }

    const answer = answerByStep.get(step.id);
    if (!answer) {
      // Unanswered question — this is where the respondent resumes.
      nextStepId = step.id;
      break;
    }

    visitedStepIds.push(step.id);
    validAnswers.push(answer);

    const outcome = evaluateBranching(step, answer.value);
    if (outcome.kind === 'skip_to_outro') {
      reachedOutro = true;
      break;
    }
    if (outcome.kind === 'go_to') {
      const targetIdx = indexById.get(outcome.targetStepId);
      if (targetIdx === undefined) {
        // Dangling target (only possible on unvalidated input) — stop safely.
        reachedOutro = true;
        break;
      }
      cursor = targetIdx;
    } else {
      cursor += 1;
    }
  }

  const validStepIds = new Set(validAnswers.map((a) => a.stepId));
  const truncatedAnswerStepIds = answers
    .map((a) => a.stepId)
    .filter((id) => !validStepIds.has(id));

  return { visitedStepIds, validAnswers, truncatedAnswerStepIds, nextStepId, reachedOutro };
}
